import {
  GetInteractionRoomRoomIdStatusOptions,
  GetInteractionRoomRoomIdStatusResult,
  getInteractionRoomRoomIdStatus,
  GetInteractionRoomRoomIdStatusFields,
} from '../fetchers/interaction_room/room_id/status';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { assignMswData, useInternalState, serializeQuery } from '../hooks/internalState';
import { useCallback } from 'react';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetInteractionRoomRoomIdStatusOptions<Fields extends GetInteractionRoomRoomIdStatusFields> =
  GetInteractionRoomRoomIdStatusOptions<Fields>;

/**
 * Get the current status of an interaction room
 * This method returns the current status of the specified interaction room.
 */

export function useGetInteractionRoomRoomIdStatus<
  Fields extends GetInteractionRoomRoomIdStatusFields,
>(
  getOptions:
    | UseGetInteractionRoomRoomIdStatusOptions<Fields>
    | (() => UseGetInteractionRoomRoomIdStatusOptions<Fields> | null),
  config?: SWRConfiguration<GetInteractionRoomRoomIdStatusResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/interaction_rooms/${options.where.roomType}/${
          options.where.roomId
        }/status${serializeQuery(options)}`
      : () => null,
    options
      ? () => {
          return getInteractionRoomRoomIdStatus({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetInteractionRoomRoomIdStatus, {
    endpoint: '/interaction_rooms/:roomType/:roomId/status',
    method: 'GET',
  });
}

/**
 * Get the current status of an interaction room
 * This method returns the current status of the specified interaction room.
 */

export function useGetInteractionRoomRoomIdStatusLazy<
  Fields extends GetInteractionRoomRoomIdStatusFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    GetInteractionRoomRoomIdStatusResult<Fields> | undefined
  >();

  let callback = useCallback(
    async (options: UseGetInteractionRoomRoomIdStatusOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/interaction_rooms/${options.where.roomType}/${
            options.where.roomId
          }/status${serializeQuery(options)}`,
          getInteractionRoomRoomIdStatus({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetInteractionRoomRoomIdStatusLazy, {
    endpoint: '/interaction_rooms/:roomType/:roomId/status',
    method: 'GET',
  });
}
